import { useState } from 'react'
import { Check, ChevronDown, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'

interface CompanyOption {
  id: string
  name: string
}

interface Props {
  companies: CompanyOption[]
  /** Ids selecionados. Vazio = todas as empresas. */
  value: string[]
  onChange: (next: string[]) => void
}

/**
 * Seletor de MÚLTIPLAS empresas (Popover + Command + checkbox visual).
 * Vazio = "Todas as empresas". Marcar 1+ filtra o dashboard pra elas.
 * Estilizado pra casar com o <SelectTrigger> dos outros filtros (dark + laranja).
 */
export function MultiCompanySelect({ companies, value, onChange }: Props) {
  const [open, setOpen] = useState(false)

  // Ordena alfabético (pt-BR p/ acentos) — a ordem crua vem por createdAt,
  // o que escondia empresas importantes (ex.: FCamara) no fim do scroll.
  const sorted = [...companies].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))

  function toggle(id: string) {
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id])
  }

  const label =
    value.length === 0
      ? 'Todas as empresas'
      : value.length === 1
        ? (companies.find((c) => c.id === value[0])?.name ?? '1 empresa')
        : `${value.length} empresas`

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span
            className={cn('line-clamp-1 text-left', value.length === 0 && 'text-muted-foreground')}
          >
            {label}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar empresa..." />
          <CommandList>
            <CommandEmpty>Nenhuma empresa encontrada.</CommandEmpty>
            {value.length > 0 && (
              <>
                <CommandGroup>
                  <CommandItem onSelect={() => onChange([])} className="text-muted-foreground">
                    <X className="mr-2 h-4 w-4" />
                    Limpar seleção (todas)
                  </CommandItem>
                </CommandGroup>
                <CommandSeparator />
              </>
            )}
            <CommandGroup>
              {sorted.map((c) => {
                const checked = value.includes(c.id)
                return (
                  <CommandItem key={c.id} value={c.name} onSelect={() => toggle(c.id)}>
                    <div
                      className={cn(
                        'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                        checked ? 'bg-primary text-primary-foreground' : 'opacity-60'
                      )}
                    >
                      {checked && <Check className="h-3 w-3" />}
                    </div>
                    <span className="line-clamp-1">{c.name}</span>
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
